import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';

import { Paper, Box, CircularProgress } from '@mui/material';

import OrderBookRow from './OrderBookRow';
import { subscribeOrderBookSSE } from './orderBookSSE';
import type { OrderBook as OrderBookType, OrderBookLevel } from '../../types/orderBook';

interface OrderRow {
  level: OrderBookLevel;
  side: 'bid' | 'ask';
  index: number;
}

type Props = {
  securityKey: string;
  isLoading?: boolean;
  depth?: number;
  onPriceRangeChange?: (minPrice: number, maxPrice: number) => void;
  onPriceSelect?: (price: number) => void;
};

export const OrderBook: React.FC<Props> = ({
  securityKey,
  depth = 0,
  isLoading = false,
  onPriceRangeChange,
  onPriceSelect,
}) => {
  const [orderBook, setOrderBook] = useState<OrderBookType | null>(null);
  const [hovered, setHovered] = useState<{ side: 'bid' | 'ask'; index: number } | null>(null);
  const [active, setActive] = useState<{ side: 'bid' | 'ask'; index: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameId = useRef<number | null>(null);
  const latestOrderBook = useRef<OrderBookType | null>(null);

  const { rows, maxAsk, maxBid, dividerIndex } = useMemo(() => {
    if (!orderBook) return { rows: [], maxAsk: 1, maxBid: 1, dividerIndex: 0 };

    const asks = orderBook.asks
      .sort((a, b) => Number(b.price) - Number(a.price))
      .map((level, i) => ({ level, side: 'ask' as const, index: i }));

    const bids = orderBook.bids
      .sort((a, b) => Number(b.price) - Number(a.price))
      .map((level, i) => ({ level, side: 'bid' as const, index: i }));

    const allRows: OrderRow[] = [
      ...asks,
      // empty element which will be skipped during the render divider row
      { level: { price: '0', quantity: '0' }, side: 'bid' as const, index: -1 },
      ...bids,
    ];

    const maxAsk = Math.max(...asks.map((a) => Number(a.level.quantity)), 1);
    const maxBid = Math.max(...bids.map((b) => Number(b.level.quantity)), 1);

    const dividerIndex = asks.length; // Индекс разделителя между ask и bid

    let minPrice = Number.MIN_SAFE_INTEGER;
    if (bids.length) {
      minPrice = Math.min(...bids.map((b) => Number(b.level.price)));
    }

    let maxPrice = Number.MAX_SAFE_INTEGER;
    if (bids.length) {
      maxPrice = Math.max(...asks.map((a) => Number(a.level.price)));
    }

    if (onPriceRangeChange) {
      onPriceRangeChange(minPrice, maxPrice);
    }

    return { rows: allRows, maxAsk, maxBid, dividerIndex };
  }, [orderBook, onPriceRangeChange]);

  const handleRowClick = useCallback(
    (side: 'bid' | 'ask', index: number) => {
      setActive((prev) => {
        const newActive = prev && prev.side === side && prev.index === index ? null : { side, index };

        if (newActive && onPriceSelect) {
          const row = rows.find((r) => r.side === side && r.index === index);
          if (row) {
            onPriceSelect(Number(row.level.price));
          }
        }

        return newActive;
      });
    },
    [rows, onPriceSelect],
  );

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent, side: 'bid' | 'ask', index: number) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handleRowClick(side, index);
      }
    },
    [handleRowClick],
  );

  useEffect(() => {
    const onData = (data: OrderBookType) => {
      latestOrderBook.current = data;

      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }

      animationFrameId.current = requestAnimationFrame(() => {
        setOrderBook(latestOrderBook.current);
      });
    };

    const unsubscribe = subscribeOrderBookSSE(securityKey, depth, onData);

    return () => {
      unsubscribe();
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [securityKey, depth]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActive(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (isLoading) {
    return (
      <Paper
        elevation={1}
        sx={{
          background: '#fff',
          minWidth: 320,
          maxWidth: 330,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={1}
      sx={{ background: '#fff', minWidth: 320, maxWidth: 330, boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)' }}
    >
      <Box ref={containerRef} sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', mb: 1, px: 2, pt: 2 }} className="limit-order-book">
          <Box sx={{ width: '30%', color: '#040506', fontWeight: 500 }}>Котировка</Box>
          <Box
            sx={{
              width: '70%',
              textAlign: 'right',
              color: 'rgba(121, 143, 179, 1)',
              fontWeight: 500,
            }}
          >
            Лоты
          </Box>
        </Box>
        <Box>
          {rows.map((row, i) => {
            const isDivider = i === dividerIndex;
            const isHovered = hovered && hovered.side === row.side && hovered.index === row.index;
            const isActive = active && active.side === row.side && active.index === row.index;
            const isSelected = isActive || (!active && isHovered);

            if (isDivider) {
              return (
                <Box
                  key={`divider-${i}`}
                  sx={{
                    height: 28,
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease-in-out',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                  }}
                />
              );
            }

            return (
              <OrderBookRow
                key={`${row.side}-${row.index}`}
                row={row}
                isSelected={!!isSelected}
                maxAsk={maxAsk}
                maxBid={maxBid}
                onMouseEnter={() => setHovered({ side: row.side, index: row.index })}
                onMouseLeave={() => setHovered(null)}
                onClick={() => handleRowClick(row.side, row.index)}
                onKeyDown={(e) => handleRowKeyDown(e, row.side, row.index)}
              />
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};
